import React from 'react';
import { ButtonGroup, TextButton } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import { useI18n } from '../../i18n';
import { useAppContext } from '../../context/Context';
import { useTrackingManager } from '../../context/useTrackingManager';

type ExploreCompareButtonsProps = {
  inFoldout?: boolean;
  isFoldoutOpen?: boolean;
};

const ButtonWrapper = styled.div<{ show: boolean | undefined }>`
  margin-top: ${({ theme }) => theme.spacing.fixed350};
  margin-inline: var(--page-margin);
  display: ${(props) => (props.show ? 'none' : 'block')};
`;

const InflyoutWrapper = styled.div<{ show: boolean | undefined }>`
  margin-top: ${({ theme }) => theme.spacing.fixed300};
  display: ${(props) => (props.show ? 'block' : 'none')};
`;

const StyledTextButton = styled(TextButton)`
  white-space: nowrap;
  @media (max-width: ${(props) => props.theme.breakpoints.sm}px) {
    width: 100%;
  }
`;

export const ExploreCompareButtons = ({ inFoldout, isFoldoutOpen }: ExploreCompareButtonsProps) => {
  const explore = useI18n('exploreModels');
  const compare = useI18n('compareModels');
  const { exploreModelsUrl, compareModelsUrl, selectedFilter } = useAppContext();

  const hasExplore = Boolean(exploreModelsUrl?.length);
  const hasCompare = Boolean(compareModelsUrl?.length);
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { trackCTAClick } = useTrackingManager();
  if (!hasExplore && !hasCompare) return null;

  const trackingVal = inFoldout ? 'group tile opened' : 'group tile closed';

  const renderButtons = () => {
    // Both buttons
    if (exploreModelsUrl?.length && compareModelsUrl?.length) {
      return (
        <ButtonGroup mode="hug">
          <StyledTextButton
            data-testid="discoverButton"
            variant="secondary"
            size="sm"
            href={exploreModelsUrl}
            onClick={() => {
              trackCTAClick(
                exploreModelsUrl,
                explore,
                'all models',
                trackingVal,
                selectedFilter,
                'button',
                0,
              );
            }}
          >
            {explore}
          </StyledTextButton>
          <StyledTextButton
            data-testid="compareButton"
            variant="secondary"
            size="sm"
            href={compareModelsUrl || ''}
            onClick={() => {
              trackCTAClick(
                compareModelsUrl,
                compare,
                'compare models',
                trackingVal,
                selectedFilter,
                'button',
                0,
              );
            }}
          >
            {compare}
          </StyledTextButton>
        </ButtonGroup>
      );
      // Only explore
    } else if (exploreModelsUrl?.length) {
      return (
        <StyledTextButton
          data-testid="discoverButton"
          variant="secondary"
          size="sm"
          href={exploreModelsUrl || ''}
          onClick={() => {
            trackCTAClick(
              exploreModelsUrl,
              explore,
              'all models',
              trackingVal,
              selectedFilter,
              'button',
              0,
            );
          }}
        >
          {explore}
        </StyledTextButton>
      );
    } else if (compareModelsUrl?.length) {
      return (
        <StyledTextButton
          data-testid="compareButton"
          variant="secondary"
          size="sm"
          href={compareModelsUrl}
          onClick={() => {
            trackCTAClick(
              compareModelsUrl,
              compare,
              'compare models',
              trackingVal,
              selectedFilter,
              'button',
              0,
            );
          }}
        >
          {compare}
        </StyledTextButton>
      );
    } else {
      return <></>;
    }
  };

  return inFoldout ? (
    <InflyoutWrapper show={isFoldoutOpen}>{renderButtons()}</InflyoutWrapper>
  ) : (
    <ButtonWrapper show={isFoldoutOpen}>{renderButtons()}</ButtonWrapper>
  );
};

ExploreCompareButtons.displayName = 'ExploreCompareButtons';

export default ExploreCompareButtons;
